import {useChangeLocale} from '@common/locale-switcher/change-locale';
import {getBootstrapData, useBootstrapDataStore} from '@ui/bootstrap-data/bootstrap-data-store';
import {Button} from '@ui/buttons/button';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {KeyboardArrowDownIcon} from '@ui/icons/material/KeyboardArrowDown';
import {Menu, MenuItem, MenuTrigger} from '@ui/menu/menu-trigger';
import {useSettings} from '@ui/settings/use-settings';

// Language code → country flag code. Most codes map to themselves; the map
// below covers cases where they don't (en → us, ar → eg, …).
const LANG_FLAG_MAP: Record<string, string> = {
  en: 'us', ar: 'eg', zh: 'cn', ja: 'jp', ko: 'kr', he: 'il',
  cs: 'cz', el: 'gr', fa: 'ir', uk: 'ua', vi: 'vn', ms: 'my',
};

function flagFor(lang: string): string {
  const lc = lang.toLowerCase();
  return LANG_FLAG_MAP[lc] || lc;
}

function LocaleFlag({lang, size = 'h-12 w-16'}: {lang: string; size?: string}) {
  const baseUrl = getBootstrapData().settings.base_url || '';
  return (
    <img
      src={`${baseUrl}/flags/${flagFor(lang)}.svg`}
      className={`${size} shrink-0 rounded-sm border border-divider object-cover`}
      style={{height: 14, width: 20}}
      alt={lang}
      loading="lazy"
    />
  );
}

export function LocaleSwitcher({compact}: {compact?: boolean} = {}) {
  const {localeCode, localeName} = useSelectedLocale();
  const changeLocale = useChangeLocale();
  const siteLocales = useBootstrapDataStore(s => s.data.i18n.locales);
  const {i18n} = useSettings();

  if (!siteLocales || !localeCode || !i18n.enable) return null;

  // Sort locales A → Z by code for a predictable menu order.
  const sortedLocales = [...siteLocales].sort((a, b) => a.language.localeCompare(b.language));

  return (
    <MenuTrigger
      floatingWidth={compact ? 'auto' : 'matchTrigger'}
      selectionMode="single"
      selectedValue={localeCode}
      onSelectionChange={value => {
        const newLocale = value as string;
        if (newLocale !== localeCode) {
          changeLocale.mutate({locale: newLocale});
        }
      }}
    >
      {/* Compact: just the flag (the full language name shows in the dropdown). */}
      <Button
        disabled={changeLocale.isPending}
        className={compact ? undefined : 'capitalize'}
        aria-label={localeName}
        startIcon={<LocaleFlag lang={localeCode} />}
        endIcon={compact ? undefined : <KeyboardArrowDownIcon />}
      >
        {compact ? null : localeName}
      </Button>
      <Menu>
        {sortedLocales.map(locale => (
          <MenuItem
            value={locale.language}
            key={locale.language}
            className="capitalize"
            startIcon={<LocaleFlag lang={locale.language} />}
          >
            {locale.name}
          </MenuItem>
        ))}
      </Menu>
    </MenuTrigger>
  );
}
